import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { FileText, Upload, ExternalLink, Pencil, Trash2, X, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import {
  deleteYatraPdf,
  documentUrl,
  listYatraPdfs,
  saveYatraPdf,
  type YatraPdf,
} from "../lib/jain-yatra-pdf-api";

export const Route = createFileRoute("/admin/jain-yatra-pdf")({ component: Page });

function fileName(pdf: string) {
  try {
    return decodeURIComponent(
      new URL(pdf, "https://example.com").pathname.split("/").pop() || "Yatra document",
    );
  } catch {
    return "Yatra document";
  }
}

function Page() {
  const qc = useQueryClient();
  const [editor, setEditor] = useState<{ document?: YatraPdf } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [removing, setRemoving] = useState<YatraPdf | null>(null);
  const query = useQuery({
    queryKey: ["jgy-yatra-pdfs"],
    queryFn: ({ signal }) => listYatraPdfs(signal),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["jgy-yatra-pdfs"] });
  const onError = (error: unknown) =>
    toast.error(error instanceof Error ? error.message : "Something went wrong.");
  const save = useMutation({
    mutationFn: async ({ file: upload, id }: { file: File; id?: number }) => {
      if (id === undefined) {
        const documents = await listYatraPdfs();
        qc.setQueryData(["jgy-yatra-pdfs"], documents);
        if (documents.length > 0) {
          setEditor(null);
          setFile(null);
          throw new Error("A document already exists. Use Replace to update it.");
        }
      }
      await saveYatraPdf(upload, id);
    },
    onSuccess: () => {
      setEditor(null);
      setFile(null);
      toast.success("Document saved.");
      return refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: deleteYatraPdf,
    onSuccess: () => {
      setRemoving(null);
      toast.success("Document deleted.");
      return refresh();
    },
    onError,
  });
  const busy = save.isPending || remove.isPending;
  const canUpload = query.isSuccess && !query.isFetching && query.data.length === 0 && !busy;
  const openEditor = (document?: YatraPdf) => {
    if (busy || (!document && !canUpload)) {
      return;
    }
    setFile(null);
    setEditor({ document });
  };
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
            Yatra resources
          </p>
          <h2 className="font-serif text-3xl font-bold">Jain Yatra PDF</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage your yatra document. Only one document can be uploaded at a time.
          </p>
        </div>
        {query.isSuccess && query.data.length === 0 && (
          <button
            className="btn-primary py-2.5 text-sm"
            disabled={!canUpload}
            onClick={() => openEditor()}
          >
            <Upload className="h-4 w-4" /> Upload document
          </button>
        )}
      </header>
      <section
        className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
        aria-label="Yatra document"
        aria-busy={query.isFetching}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-5">
          <h3 className="font-semibold">
            Yatra document{" "}
            <span className="ml-2 rounded-md bg-primary/10 px-2 py-1 text-xs text-primary">
              {query.data?.length ?? "—"}
            </span>
          </h3>
          <span className="text-xs text-muted-foreground">PDF & SVG</span>
        </div>
        {query.isPending && (
          <div className="flex items-center justify-center gap-2 p-16 text-sm text-muted-foreground">
            <LoaderCircle className="h-5 w-5 animate-spin" /> Loading document...
          </div>
        )}
        {query.isError && (
          <div className="space-y-3 p-10 text-center" role="alert">
            <p className="text-sm text-destructive">{query.error.message}</p>
            <button className="btn-outline py-2 text-sm" onClick={() => void query.refetch()}>
              Try again
            </button>
          </div>
        )}
        {!query.isError && query.data?.length === 0 && (
          <div className="flex flex-col items-center px-6 py-16 text-center">
            <span className="mb-4 rounded-2xl bg-primary/10 p-5 text-primary">
              <FileText className="h-8 w-8" strokeWidth={1.5} />
            </span>
            <h3 className="font-serif text-xl">No document uploaded</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Upload a yatra document to get started.
            </p>
            <button
              className="btn-outline mt-5 py-2 text-sm"
              disabled={!canUpload}
              onClick={() => openEditor()}
            >
              Choose a document
            </button>
          </div>
        )}
        {!query.isError && (
          <div className="divide-y divide-border">
            {query.data?.map((doc) => {
              const url = documentUrl(doc.pdf);
              return (
                <article
                  key={doc.id}
                  className="flex flex-wrap items-center gap-4 p-5 transition-colors hover:bg-muted/20 sm:px-6"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/10 bg-primary/5 text-primary">
                    <FileText className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="break-all text-sm font-semibold">{fileName(doc.pdf)}</h4>
                    <p className="mt-1 text-xs text-muted-foreground">Document #{doc.id}</p>
                  </div>
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline py-2 text-xs"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> View
                      </a>
                    )}
                    <button
                      className="btn-outline py-2 text-xs"
                      disabled={busy}
                      onClick={() => openEditor(doc)}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Replace
                    </button>
                    <button
                      className="rounded-lg p-2 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                      aria-label={`Delete ${fileName(doc.pdf)}`}
                      disabled={busy}
                      onClick={() => setRemoving(doc)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      <Dialog.Root
        open={!!editor}
        onOpenChange={(open) => {
          if (!open && !save.isPending) {
            setEditor(null);
            setFile(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%_-_2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <Dialog.Title className="font-serif text-2xl font-bold">
              {editor?.document ? "Replace document" : "Upload document"}
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-muted-foreground">
              {editor?.document
                ? "Choose a new file for this document."
                : "Upload your yatra document as a PDF or SVG file."}
            </Dialog.Description>
            <Dialog.Close
              aria-label="Close"
              disabled={save.isPending}
              className="absolute right-4 top-4 rounded-lg p-1 hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
            <form
              className="mt-6 space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (file && !save.isPending && (editor?.document || canUpload)) {
                  save.mutate({ file, id: editor?.document?.id });
                }
              }}
            >
              <label className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border border-dashed border-primary/30 bg-primary/5 px-4 py-8 text-center">
                <Upload className="h-7 w-7 text-primary" strokeWidth={1.5} />
                <span className="text-sm font-semibold">{file ? file.name : "Choose a file"}</span>
                <span className="text-xs text-muted-foreground">PDF or SVG</span>
                <input
                  className="max-w-full text-xs"
                  type="file"
                  accept=".pdf,.svg,application/pdf,image/svg+xml"
                  disabled={save.isPending}
                  required
                  onChange={(e) => {
                    const selected = e.target.files?.[0] ?? null;
                    if (selected && (!/\.(pdf|svg)$/i.test(selected.name) || selected.size === 0)) {
                      toast.error("Choose a non-empty PDF or SVG file.");
                      e.target.value = "";
                      setFile(null);
                      return;
                    }
                    setFile(selected);
                  }}
                />
              </label>
              <button
                className="btn-primary w-full py-2.5 text-sm"
                type="submit"
                disabled={!file || save.isPending || (!editor?.document && !canUpload)}
              >
                {save.isPending
                  ? "Saving..."
                  : editor?.document
                    ? "Save replacement"
                    : "Upload document"}
              </button>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={!!removing}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) {
            setRemoving(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%_-_2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <Dialog.Title className="font-serif text-xl font-bold">Delete document?</Dialog.Title>
            <Dialog.Description className="mt-3 break-words text-sm text-muted-foreground">
              Remove {removing ? fileName(removing.pdf) : "this document"} from the library? This
              action cannot be undone.
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-3">
              <Dialog.Close className="btn-outline py-2 text-sm" disabled={remove.isPending}>
                Cancel
              </Dialog.Close>
              <button
                className="rounded-full bg-destructive px-5 py-2 text-sm font-semibold text-destructive-foreground disabled:opacity-50"
                disabled={remove.isPending}
                onClick={() => {
                  if (removing) {
                    remove.mutate(removing.id);
                  }
                }}
              >
                {remove.isPending ? "Deleting..." : "Delete document"}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
